import * as React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import * as global from "../../data/global";

const MENU_MAX_HEIGHT = "min(70vh, 520px)";

const HIDE_SCROLLBAR_SX = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
};

const MENU_ITEM_WRAP_SX = {
    "& .MuiMenuItem-root": {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        lineHeight: 1.45,
        py: 1,
        px: 2,
    },
};

/** Inset 1px hairline. Neutralize CssBaseline’s full-width `hr` fill. */
const menuDividerSx = (isLight) => ({
    "& .MuiDivider-root": {
        border: "none",
        height: "1px",
        mx: 2,
        my: 0.5,
        backgroundColor: isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(244, 204, 142, 0.35)",
        opacity: 1,
    },
});

const readScrollHints = (scroller) => {
    if (!scroller) return { up: false, down: false };
    const { scrollTop, scrollHeight, clientHeight } = scroller;
    return {
        up: scrollTop > 2,
        down: scrollTop + clientHeight < scrollHeight - 2,
    };
};

/**
 * Popover/Menu paper with a hidden scrollbar and ▲/▼ bars when the list can scroll.
 */
const ScrollHintPaper = React.forwardRef(function ScrollHintPaper(props, ref) {
    const { themeMode, ownerState, children, sx, className, style, ...paperProps } = props;
    const isLight = (themeMode || global.gw.theme) === "light";
    const arrowColor = isLight ? "#333333" : "#f4cc8e";
    const barBg = isLight ? "#ffffff" : "#2d2d2d";
    const scrollerRef = React.useRef(null);
    const [hints, setHints] = React.useState({ up: false, down: false });

    const measure = React.useCallback(() => {
        setHints(readScrollHints(scrollerRef.current));
    }, []);

    React.useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        measure();
        el.addEventListener("scroll", measure, { passive: true });
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        ro?.observe(el);
        const list = el.querySelector(".MuiList-root, ul");
        if (list) ro?.observe(list);
        window.addEventListener("resize", measure);
        const raf = requestAnimationFrame(measure);
        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener("scroll", measure);
            ro?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [measure, children]);

    const hintBarSx = (visible) => ({
        flex: "0 0 auto",
        height: visible ? 20 : 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: arrowColor,
        backgroundColor: barBg,
        fontSize: 11,
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
    });

    const { onWheel, ...restPaperProps } = paperProps;

    return (
        <Paper
            ref={ref}
            className={className ? `${className} hide-scrollbar` : "hide-scrollbar"}
            {...restPaperProps}
            onWheel={(e) => {
                onWheel?.(e);
                e.stopPropagation();
            }}
            style={{
                ...(style || {}),
                overflow: "hidden",
                overflowY: "hidden",
                overflowX: "hidden",
                maxHeight: MENU_MAX_HEIGHT,
            }}
            sx={{
                position: "absolute",
                outline: 0,
                display: "flex",
                flexDirection: "column",
                minWidth: 180,
                maxWidth: "min(92vw, 520px)",
                ...HIDE_SCROLLBAR_SX,
                ...sx,
                overflow: "hidden !important",
                overflowY: "hidden !important",
                maxHeight: MENU_MAX_HEIGHT,
            }}
        >
            <Box aria-hidden sx={hintBarSx(hints.up)}>
                ▲
            </Box>
            <Box
                ref={scrollerRef}
                onScroll={measure}
                className="hide-scrollbar"
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    overscrollBehavior: "contain",
                    ...HIDE_SCROLLBAR_SX,
                    ...MENU_ITEM_WRAP_SX,
                    ...menuDividerSx(isLight),
                }}
            >
                {children}
            </Box>
            <Box aria-hidden sx={hintBarSx(hints.down)}>
                ▼
            </Box>
        </Paper>
    );
});

export default ScrollHintPaper;

/**
 * In-flow list scroller with hidden scrollbar and ▲/▼ bars.
 * Use inside a Popover/Menu when replacing the paper slot is not reliable.
 */
export function ScrollHintBody({ children, maxHeight = 360, themeMode }) {
    const isLight = (themeMode || global.gw.theme) === "light";
    const arrowColor = isLight ? "#333333" : "#f4cc8e";
    const barBg = isLight ? "#ffffff" : "#2d2d2d";
    const scrollerRef = React.useRef(null);
    const [hints, setHints] = React.useState({ up: false, down: false });

    const measure = React.useCallback(() => {
        setHints(readScrollHints(scrollerRef.current));
    }, []);

    React.useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        measure();
        el.addEventListener("scroll", measure, { passive: true });
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        ro?.observe(el);
        if (el.firstElementChild) ro?.observe(el.firstElementChild);
        window.addEventListener("resize", measure);
        const raf = requestAnimationFrame(measure);
        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener("scroll", measure);
            ro?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [measure, children]);

    const hintBarSx = (visible) => ({
        flex: "0 0 auto",
        height: visible ? 20 : 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: arrowColor,
        backgroundColor: barBg,
        fontSize: 11,
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
    });

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight,
                overflow: "hidden",
                width: "100%",
            }}
        >
            <Box aria-hidden sx={hintBarSx(hints.up)}>
                ▲
            </Box>
            <Box
                ref={scrollerRef}
                onScroll={measure}
                className="hide-scrollbar"
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    overscrollBehavior: "contain",
                    ...HIDE_SCROLLBAR_SX,
                    ...MENU_ITEM_WRAP_SX,
                    ...menuDividerSx(isLight),
                }}
            >
                {children}
            </Box>
            <Box aria-hidden sx={hintBarSx(hints.down)}>
                ▼
            </Box>
        </Box>
    );
}

/**
 * Merge caller Menu/Select MenuProps with ScrollHintPaper, hidden scrollbars,
 * and a list that does not clip the last row.
 */
export function withScrollHintMenuProps(menuProps = {}, themeMode) {
    const mode = themeMode || global.gw?.theme;
    const {
        slots,
        slotProps,
        PaperProps,
        MenuListProps,
        ...rest
    } = menuProps || {};
    return {
        disableScrollLock: rest.disableScrollLock ?? true,
        marginThreshold: rest.marginThreshold ?? 12,
        ...rest,
        slots: {
            paper: ScrollHintPaper,
            ...slots,
        },
        slotProps: {
            ...slotProps,
            paper: {
                themeMode: mode,
                ...(slotProps?.paper || {}),
            },
        },
        PaperProps: {
            className: ["hide-scrollbar", PaperProps?.className].filter(Boolean).join(" "),
            themeMode: mode,
            ...PaperProps,
            style: {
                ...(PaperProps?.style || {}),
                maxHeight: MENU_MAX_HEIGHT,
            },
        },
        MenuListProps: {
            ...MenuListProps,
            className: ["hide-scrollbar", MenuListProps?.className].filter(Boolean).join(" "),
            sx: {
                py: 0.5,
                ...(MenuListProps?.sx || {}),
                overflow: "visible !important",
                overflowY: "visible !important",
                maxHeight: "none",
                ...HIDE_SCROLLBAR_SX,
                ...MENU_ITEM_WRAP_SX,
                ...menuDividerSx(mode === "light"),
            },
        },
    };
}

export function getScrollHintMenuProps(themeMode) {
    return withScrollHintMenuProps({}, themeMode);
}

/** Spread into createTheme `components` so Menu/Select inherit scroll-hint paper. */
export function withMenuScrollHintTheme(components = {}) {
    const menuProps = getScrollHintMenuProps();
    const hint = {
        MuiMenu: {
            defaultProps: menuProps,
        },
        MuiSelect: {
            defaultProps: {
                MenuProps: menuProps,
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.45,
                },
            },
        },
    };
    return {
        ...hint,
        ...components,
        MuiMenu: {
            ...hint.MuiMenu,
            ...components.MuiMenu,
            defaultProps: {
                ...hint.MuiMenu.defaultProps,
                ...components.MuiMenu?.defaultProps,
            },
        },
        MuiSelect: {
            ...hint.MuiSelect,
            ...components.MuiSelect,
            defaultProps: {
                ...hint.MuiSelect.defaultProps,
                ...components.MuiSelect?.defaultProps,
            },
            styleOverrides: {
                ...hint.MuiSelect.styleOverrides,
                ...components.MuiSelect?.styleOverrides,
            },
        },
        MuiMenuItem: {
            ...hint.MuiMenuItem,
            ...components.MuiMenuItem,
            styleOverrides: {
                ...hint.MuiMenuItem.styleOverrides,
                ...components.MuiMenuItem?.styleOverrides,
                root: {
                    ...hint.MuiMenuItem.styleOverrides.root,
                    ...components.MuiMenuItem?.styleOverrides?.root,
                },
            },
        },
    };
}
